import { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Zap, Globe, AlertTriangle, ChevronDown, ChevronRight, ArrowRightLeft, Info, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { apiPost } from "@/lib/api";

// --- Tagging Logic (Step 1) ---
function tagEmission(em, reassignments = {}) {
  const override = reassignments[em.id];

  let control_tag;
  if (override) {
    control_tag = override;
  } else if (em.ownership === "owned" || em.ownership === "controlled") {
    control_tag = "direct";
  } else if (em.ownership === "purchased") {
    control_tag = "energy";
  } else if (em.ownership === "external") {
    control_tag = "value_chain";
  } else {
    control_tag = "uncertain";
  }

  let energy_tag;
  if (em.source_type === "fuel") energy_tag = "direct_emission";
  else if (em.source_type === "electricity") energy_tag = "indirect_energy";
  else energy_tag = "embedded";

  const scope = control_tag === "direct" ? 1 : control_tag === "energy" ? 2 : control_tag === "value_chain" ? 3 : null;

  return { ...em, control_tag, energy_tag, value_chain_tag: em.value_chain_position, scope };
}

// --- Cluster Aggregation (Step 2) ---
function aggregateClusters(tagged) {
  const clusters = {
    direct: { items: [], total: 0 },
    energy: { items: [], total: 0 },
    value_chain: { items: [], total: 0 },
    uncertain: { items: [], total: 0 },
  };
  tagged.forEach((em) => {
    const c = clusters[em.control_tag];
    if (c) { c.items.push(em); c.total += em.emission_value; }
  });
  const total = tagged.reduce((s, em) => s + em.emission_value, 0);
  return {
    clusters,
    total,
    direct_pct: total > 0 ? (clusters.direct.total / total) * 100 : 0,
    energy_pct: total > 0 ? (clusters.energy.total / total) * 100 : 0,
    value_chain_pct: total > 0 ? (clusters.value_chain.total / total) * 100 : 0,
  };
}

const CLUSTER_CONFIG = {
  direct: {
    label: "Direct Operations",
    scopeLabel: "Scope 1",
    icon: Flame,
    color: "bg-amber-500",
    colorLight: "bg-amber-500/15",
    text: "text-amber-400",
    border: "border-amber-500/30",
    tooltip: "Classified as Scope 1 because the activity is owned or directly controlled.",
  },
  energy: {
    label: "Energy Consumption",
    scopeLabel: "Scope 2",
    icon: Zap,
    color: "bg-blue-500",
    colorLight: "bg-blue-500/15",
    text: "text-blue-400",
    border: "border-blue-500/30",
    tooltip: "Classified as Scope 2 because emissions come from purchased energy.",
  },
  value_chain: {
    label: "Value Chain Impact",
    scopeLabel: "Scope 3",
    icon: Globe,
    color: "bg-emerald-500",
    colorLight: "bg-emerald-500/15",
    text: "text-emerald-400",
    border: "border-emerald-500/30",
    tooltip: "Classified as Scope 3 because the activity lies in the value chain.",
  },
};

function formatKg(val) {
  if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `${(val / 1000).toFixed(1)}k`;
  return `${val}`;
}

function getEmissionComparison(totalKg) {
  if (totalKg < 5000)
    return "Roughly what a single mature oak tree absorbs in a year";
  if (totalKg < 20000)
    return `Like the CO\u2082 absorbed by a pitch the size of Santiago Bernab\u00e9u covered in forest`;
  if (totalKg < 100000)
    return `Equivalent to the annual CO\u2082 captured by ${Math.round(totalKg / 7000)} football fields of Amazon rainforest`;
  if (totalKg < 500000)
    return `Comparable to what Hyde Park in London absorbs in ${Math.max(1, Math.round((totalKg / 800000) * 12))} months`;
  if (totalKg < 2000000)
    return `That's what New York City's Central Park absorbs in about ${Math.max(1, Math.round((totalKg / 2500000) * 12))} months`;
  if (totalKg < 10000000)
    return `Equivalent to the annual CO\u2082 captured by ${Math.round(totalKg / 2500000)} areas the size of Central Park`;
  return `Like the yearly absorption of ${Math.round(totalKg / 50000)} hectares of the Black Forest in Germany`;
}

// --- Emission Item Row ---
function EmissionItem({ em, scopeView, onReassign }) {
  const cfg = CLUSTER_CONFIG[em.control_tag];
  const lowConf = em.confidence_score < 0.6;

  return (
    <div
      className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-white/5 transition-colors group"
      data-testid={`emission-item-${em.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm text-white truncate">{em.activity}</span>
          {lowConf && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <AlertTriangle className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />
                </TooltipTrigger>
                <TooltipContent className="bg-card border-border text-xs max-w-[200px]">
                  Low confidence estimate ({Math.round(em.confidence_score * 100)}%). Consider verifying this data.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-muted-foreground">{em.value_chain_tag}</span>
          <span className="text-xs text-muted-foreground">·</span>
          <span className="text-xs text-muted-foreground">{em.source_type}</span>
          {em.confidence_score < 0.8 && (
            <>
              <span className="text-xs text-muted-foreground">·</span>
              <span className={`text-xs ${lowConf ? 'text-yellow-500' : 'text-muted-foreground'}`}>
                {Math.round(em.confidence_score * 100)}% conf.
              </span>
            </>
          )}
        </div>
      </div>

      <div className="text-right flex-shrink-0">
        <span className="text-sm font-medium text-white">{formatKg(em.emission_value)}</span>
        <span className="text-xs text-muted-foreground ml-1">kgCO2e</span>
      </div>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge className={`${cfg?.colorLight || 'bg-white/10'} ${cfg?.text || 'text-white/50'} border-0 text-[10px] cursor-help`}>
              {scopeView ? `Scope ${em.scope || '?'}` : cfg?.label?.split(' ')[0] || '?'}
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="bg-card border-border text-xs max-w-[220px]">
            {cfg?.tooltip || "Classification uncertain."}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {lowConf && (
        <Select onValueChange={(v) => onReassign(em.id, v)} value={em.control_tag}>
          <SelectTrigger className="w-8 h-7 bg-transparent border-none p-0 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`reassign-${em.id}`}>
            <ArrowRightLeft className="w-3.5 h-3.5 text-muted-foreground" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="direct">Direct Operations</SelectItem>
            <SelectItem value="energy">Energy Consumption</SelectItem>
            <SelectItem value="value_chain">Value Chain</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

// --- Cluster Bar ---
function ClusterBar({ clusterKey, data, pct, total, scopeView, onReassign }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = CLUSTER_CONFIG[clusterKey];
  if (!cfg) return null;
  const Icon = cfg.icon;
  const label = scopeView ? cfg.scopeLabel : cfg.label;

  return (
    <div className="space-y-2" data-testid={`cluster-${clusterKey}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 group cursor-pointer"
        data-testid={`cluster-toggle-${clusterKey}`}
      >
        <div className={`w-8 h-8 rounded-lg ${cfg.colorLight} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${cfg.text}`} />
        </div>
        <div className="flex-1 text-left">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-white">{label}</span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-white font-semibold">{pct.toFixed(1)}%</span>
              <span className="text-xs text-muted-foreground">{formatKg(data.total)} kgCO2e</span>
              {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            </div>
          </div>
          {/* Bar */}
          <div className="h-3 rounded-full bg-white/5 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.max(pct, 1)}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className={`h-full rounded-full ${cfg.color}`}
            />
          </div>
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden ml-11"
          >
            <div className={`rounded-xl border ${cfg.border} bg-white/[0.02] divide-y divide-white/5`}>
              {data.items
                .sort((a, b) => b.emission_value - a.emission_value)
                .map((em) => (
                  <EmissionItem key={em.id} em={em} scopeView={scopeView} onReassign={onReassign} />
                ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// --- Main Component ---
export function ScopeEngine({ companyId }) {
  const [rawEmissions, setRawEmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scopeView, setScopeView] = useState(false);
  const [reassignments, setReassignments] = useState({});
  const [saved, setSaved] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      if (!companyId) return;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const data = await apiPost("/api/scope/generate", { company_id: companyId });
          if (!mountedRef.current) return;
          setRawEmissions(data.emissions || []);
          setLoading(false);
          return;
        } catch (err) {
          if (!mountedRef.current) return;
          if (attempt < 2) await new Promise(r => setTimeout(r, 1200 * (attempt + 1)));
          else toast.error("Could not load emission data.");
        }
      }
      if (mountedRef.current) setLoading(false);
    };
    load();
    return () => { mountedRef.current = false; };
  }, [companyId]);

  const handleReassign = (emissionId, newCluster) => {
    setReassignments((prev) => ({ ...prev, [emissionId]: newCluster }));
    setSaved(false);
  };

  const handleSave = async () => {
    try {
      await apiPost("/api/scope/save", { company_id: companyId, reassignments });
      toast.success("Emission map saved.");
      setSaved(true);
    } catch (err) {
      toast.error("Could not save.");
    }
  };

  const tagged = useMemo(() => rawEmissions.map((em) => tagEmission(em, reassignments)), [rawEmissions, reassignments]);
  const agg = useMemo(() => aggregateClusters(tagged), [tagged]);

  if (loading) {
    return (
      <div className="glass-card p-8" data-testid="scope-loading">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-1 h-8 rounded-full bg-primary" />
          <h3 className="text-lg font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Emission Map</h3>
        </div>
        <div className="mt-5 rounded-xl border border-primary/15 p-6 flex flex-col items-center gap-4" style={{ background: "linear-gradient(135deg, rgba(74,222,128,0.03) 0%, rgba(2,4,2,0.95) 100%)" }}>
          <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          <div className="text-center">
            <p className="text-white font-medium mb-1">Mapping your emission sources</p>
            <p className="text-xs text-white/40">This may take a moment — we're analyzing your company profile</p>
          </div>
          <div className="w-full max-w-xs h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full rounded-full bg-primary/50 animate-[indeterminate_1.5s_ease-in-out_infinite]" style={{ width: '40%' }} />
          </div>
        </div>
      </div>
    );
  }

  if (!rawEmissions.length) return null;

  const hasReassignments = Object.keys(reassignments).length > 0;

  return (
    <div className="space-y-6" data-testid="scope-engine">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1 h-8 rounded-full bg-primary" />
            <h3 className="text-lg font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Emission Map</h3>
          </div>
          <p className="text-sm text-white/40 ml-4">
            Click each cluster to see individual emission sources. Low-confidence items can be reassigned.
          </p>
        </div>
        {/* Toggle */}
        <div className="flex items-center gap-1 p-1 rounded-full bg-white/5 border border-white/10 self-start" data-testid="view-toggle">
          <button
            onClick={() => setScopeView(false)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${!scopeView ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-white'}`}
            data-testid="toggle-cluster"
          >
            Cluster View
          </button>
          <button
            onClick={() => setScopeView(true)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${scopeView ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-white'}`}
            data-testid="toggle-scope"
          >
            Scope View
          </button>
        </div>
      </div>

      {/* Total */}
      <div className="glass-card p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs text-muted-foreground uppercase tracking-wider">Total Estimated Emissions</span>
            <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="total-emissions">
              {formatKg(agg.total)} <span className="text-sm font-normal text-muted-foreground">kgCO2e / year</span>
            </p>
          </div>
          <div className="flex gap-4 text-center">
            {[
              { key: "direct", pct: agg.direct_pct, cfg: CLUSTER_CONFIG.direct },
              { key: "energy", pct: agg.energy_pct, cfg: CLUSTER_CONFIG.energy },
              { key: "value_chain", pct: agg.value_chain_pct, cfg: CLUSTER_CONFIG.value_chain },
            ].map(({ key, pct, cfg }) => (
              <div key={key} className="hidden md:block">
                <span className={`text-lg font-bold ${cfg.text}`}>{pct.toFixed(0)}%</span>
                <p className="text-[10px] text-muted-foreground">{scopeView ? cfg.scopeLabel : cfg.label.split(' ')[0]}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-start gap-2.5 rounded-lg bg-primary/[0.06] border border-primary/15 px-3.5 py-2.5" data-testid="emission-comparison">
          <span className="text-primary text-base leading-none mt-0.5">&#x1f331;</span>
          <p className="text-sm text-white/60 leading-relaxed"><span className="text-white/80 font-medium">Did you know?</span> {getEmissionComparison(agg.total)}</p>
        </div>
      </div>

      {/* Cluster Bars */}
      <div className="glass-card p-5 space-y-5">
        <ClusterBar clusterKey="direct" data={agg.clusters.direct} pct={agg.direct_pct} total={agg.total} scopeView={scopeView} onReassign={handleReassign} />
        <ClusterBar clusterKey="energy" data={agg.clusters.energy} pct={agg.energy_pct} total={agg.total} scopeView={scopeView} onReassign={handleReassign} />
        <ClusterBar clusterKey="value_chain" data={agg.clusters.value_chain} pct={agg.value_chain_pct} total={agg.total} scopeView={scopeView} onReassign={handleReassign} />
      </div>

      {/* Unclassified */}
      {agg.clusters.uncertain.items.length > 0 && (
        <div className="glass-card p-5 border-yellow-500/20" data-testid="unclassified-section">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            <span className="text-sm font-medium text-yellow-400">Unclassified Emissions</span>
            <span className="text-xs text-muted-foreground">({agg.clusters.uncertain.items.length} items)</span>
          </div>
          <div className="divide-y divide-white/5">
            {agg.clusters.uncertain.items.map((em) => (
              <EmissionItem key={em.id} em={em} scopeView={scopeView} onReassign={handleReassign} />
            ))}
          </div>
        </div>
      )}

      {/* Scope explanation (minimal) */}
      <div className="flex flex-wrap gap-3">
        <TooltipProvider>
          {Object.entries(CLUSTER_CONFIG).map(([key, cfg]) => (
            <Tooltip key={key}>
              <TooltipTrigger>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-help">
                  <div className={`w-2.5 h-2.5 rounded-full ${cfg.color}`} />
                  <span>{scopeView ? cfg.scopeLabel : cfg.label}</span>
                  <Info className="w-3 h-3" />
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-card border-border text-xs max-w-[250px]">
                {cfg.tooltip}
              </TooltipContent>
            </Tooltip>
          ))}
        </TooltipProvider>
      </div>

      {/* Save button */}
      {hasReassignments && (
        <Button
          data-testid="scope-save-btn"
          onClick={handleSave}
          className="rounded-full px-6 bg-primary text-primary-foreground btn-glow"
        >
          {saved ? "Saved" : <><Save className="w-4 h-4 mr-2" /> Save changes</>}
        </Button>
      )}
    </div>
  );
}
